import type { RefCallback } from 'react'
import { useMemo } from 'react'
import { useInView } from 'react-intersection-observer'

import { ChertoExperiments } from 'constants/experiments'
import useExperiment from 'hooks/Experiments/useExperiment'

type StructuredFacilitiesOffer = App.HotelOffer | App.Offer

function getStructuredFacilities(offerDetails: StructuredFacilitiesOffer): Array<App.StructuredFacilityGroup> | undefined {
  return 'structuredFacilities' in offerDetails ? offerDetails.structuredFacilities : undefined
}

interface StructuredFacilitiesReworkState {
  structuredFacilitiesReworkActivationRef: RefCallback<HTMLDivElement>
  structuredFacilitiesRework?: Array<App.StructuredFacilityGroup>
  shouldTrackFacilitiesExperimentInView: boolean
}

export interface StructuredFacilitiesActivationPlacement {
  insertIndex: number
}

/**
 * When eligible, place one shared standalone sentinel at the facilities slot index.
 * Intentionally not nested in arm-specific accordion content: nesting under the
 * Facilities heading on variant-only property pages caused the cro-facilities-rework
 * (v1) property-page SRM.
 */
export function buildStructuredFacilitiesActivationPlacement(options: {
  shouldTrackFacilitiesExperimentInView: boolean
  facilitiesSectionInsertIndex: number
}): StructuredFacilitiesActivationPlacement | undefined {
  if (!options.shouldTrackFacilitiesExperimentInView) {
    return undefined
  }

  return {
    insertIndex: options.facilitiesSectionInsertIndex,
  }
}

export function useStructuredFacilitiesReworkForFacilities(
  structuredFacilities: Array<App.StructuredFacilityGroup> | undefined,
): StructuredFacilitiesReworkState {
  const hasStructuredFacilities = !!structuredFacilities?.length
  const [structuredFacilitiesReworkActivationRef, structuredFacilitiesReworkInView] = useInView({
    triggerOnce: true,
    // Match cancellation-timeline activation: observe a fixed-height sentinel at 0.5.
    threshold: 0.5,
  })
  const experimentOptions = useMemo(() => ({
    // Only expose users who can see the structured facilities experience. Control
    // users on legacy-only offers are excluded to keep cohort denominators aligned.
    // Assignment gating stays inside useExperiment / useExperimentActivation.
    canActivate: structuredFacilitiesReworkInView && hasStructuredFacilities,
  }), [structuredFacilitiesReworkInView, hasStructuredFacilities])
  const { isAnyVariant } = useExperiment(ChertoExperiments.croFacilitiesReworkV2, experimentOptions)
  const shouldTrackFacilitiesExperimentInView = hasStructuredFacilities

  return {
    structuredFacilitiesReworkActivationRef,
    structuredFacilitiesRework: isAnyVariant && hasStructuredFacilities ? structuredFacilities : undefined,
    shouldTrackFacilitiesExperimentInView,
  }
}

export function usePropertyStructuredFacilitiesRework(
  property: App.Property.Property,
): StructuredFacilitiesReworkState {
  return useStructuredFacilitiesReworkForFacilities(property.structuredFacilities)
}

function useStructuredFacilitiesRework(offerDetails: StructuredFacilitiesOffer): StructuredFacilitiesReworkState {
  return useStructuredFacilitiesReworkForFacilities(getStructuredFacilities(offerDetails))
}

export default useStructuredFacilitiesRework
